import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import { openBookingModal } from '../components/BookingModal';
import '../styles/blog-post.css';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  "AI Strategy": "pink",
  "Revenue": "cyan",
  "Case Studies": "purple",
  "Automation": "pink",
  "Growth": "cyan",
  "Industry Insights": "purple",
  "Product Updates": "cyan",
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPost = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/blog/posts/${slug}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        const data = await res.json();
        setPost(data);

        // Fetch related posts (same category)
        const allRes = await fetch(`${API}/api/blog/posts?category=${encodeURIComponent(data.category)}`);
        const allData = await allRes.json();
        setRelatedPosts((allData.posts || []).filter(p => p.slug !== slug).slice(0, 2));
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to fetch post:', e);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="blog-post-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loader2 size={32} style={{ color: 'var(--mid)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-not-found">
          <h1>Post Not Found</h1>
          <p>The article you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/blog" className="btn-gradient">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        keywords={`${post.category}, Skye AI, AI for business, ${post.title.split(' ').slice(0, 4).join(' ')}`}
        type="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.description,
          "author": { "@type": "Organization", "name": post.author || "Skye AI" },
          "publisher": { "@type": "Organization", "name": "Skye AI LLC", "logo": { "@type": "ImageObject", "url": "https://skyeai.io/skye-logo-hero.png" } },
          "datePublished": post.created_at || post.date,
          "dateModified": post.updated_at || post.created_at || post.date,
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://skyeai.io/blog/${post.slug}` },
          "image": "https://skyeai.io/skye-logo-hero.png",
        }}
      />
      {/* Hero */}
      <section className={`blog-post-hero ${post.thumbnail}`}>
        <div className="blog-post-hero-overlay"></div>
        <div className="blog-post-hero-content">
          <Link to="/blog" className="back-to-blog">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="post-category-row">
            <span className={`post-category tag-${categoryColors[post.category] || 'pink'}`}>
              {post.category}
            </span>
          </div>
          <h1>{post.title}</h1>
          <div className="post-meta">
            <span className="meta-item">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="meta-item">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="meta-item">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="blog-post-content">
        <article className="blog-article">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </article>

        {/* CTA */}
        <div className="blog-post-cta">
          <h3>Ready to automate your business with AI?</h3>
          <p>Book a free AI Growth Planning Call and see how custom AI can scale your operations.</p>
          <button onClick={openBookingModal} className="btn-gradient">
            Book My AI Growth Planning Call <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <div className="related-posts-header">
            <h2>Related Articles</h2>
          </div>
          <div className="related-posts-grid">
            {relatedPosts.map(relatedPost => (
              <Link
                key={relatedPost.slug}
                to={`/blog/${relatedPost.slug}`}
                className="related-post-card"
              >
                <div className={`related-post-thumbnail ${relatedPost.thumbnail}`}>
                  <span className={`post-category tag-${categoryColors[relatedPost.category] || 'pink'}`}>
                    {relatedPost.category}
                  </span>
                </div>
                <div className="related-post-content">
                  <h4>{relatedPost.title}</h4>
                  <span className="related-post-meta">{relatedPost.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
